import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { sendGoogleAnalyticsEvents } from '@elfsight/lib-google-analytics';
import { WithTriggers } from '@elfsight/widget-ui';
import { useSettings } from '@wu/widget-core';
import { useRememberVisitorStorage } from '../helpers';
import { useTriggers } from './utils';

type TargetChatButton = {
  appTitle?: string;
  appLink?: string;
};

interface IChatContext {
  targetChatButton: TargetChatButton;
  setTargetChatButton: (value: TargetChatButton) => void;
  messageShowed: boolean;
  setMessageShowed: (value: boolean) => void;
  openChatWindow: boolean;
  setOpenChatWindow: (value: boolean | ((value: boolean) => boolean)) => void;
  handleToggleWindow: () => void;
}

const contextDefaultValue: IChatContext = {
  targetChatButton: {},
  setTargetChatButton: () => {},
  messageShowed: false,
  setMessageShowed: () => {},
  openChatWindow: false,
  setOpenChatWindow: () => {},
  handleToggleWindow: () => {}
};

const ChatContext = createContext<IChatContext>(contextDefaultValue);

export const ChatProvider = ({ children }: PropsWithChildren) => {
  const firstRender = useRef(true);
  const {
    rememberVisitor = 1,
    editMode,
    googleAnalytics,
    widgetId
    // displayDeactivation
  } = useSettings();
  const [messageShowed, setMessageShowed] = useState<boolean>(false);
  const [targetChatButton, setTargetChatButton] = useState<
    TargetChatButton | {}
  >({});
  const [openChatWindow, setOpenChatWindow] = useState<boolean>(false);
  const triggers = useTriggers();
  const [_, { set: setStorageRememberVisitor }] = useRememberVisitorStorage();

  const setOpenChatWindowState = (
    value: boolean | ((value: boolean) => boolean)
  ) => {
    // if (displayDeactivation) {
    //   setOpenChatWindow(false);
    //   return;
    // }
    setOpenChatWindow(value);
  };

  const handleActivateTrigger = useCallback(() => {
    setOpenChatWindowState(true);
  }, []);

  const handleToggleWindow = useCallback(() => {
    setOpenChatWindowState((prev) => {
      if (prev) {
        const rememberTime =
          Math.floor(Date.now() / 1000) + rememberVisitor * 86400;
        setStorageRememberVisitor(rememberTime);
      }

      return !prev;
    });
    setTargetChatButton({});
  }, [rememberVisitor]);

  const previousOpenChatWindow = useRef(openChatWindow);
  useEffect(() => {
    if (openChatWindow && !previousOpenChatWindow.current) {
      sendGoogleAnalyticsEvents({
        googleAnalyticsEvents: googleAnalytics,
        eventType: 'widgetOpened',
        valueMapping: {
          widgetId
        }
      });
    }

    previousOpenChatWindow.current = openChatWindow;
  }, [openChatWindow, googleAnalytics, widgetId]);

  useEffect(() => {
    if (!editMode || !firstRender.current) return;

    const timeout = setTimeout(() => {
      firstRender.current = false;
      setOpenChatWindowState(true);
    }, 300);

    return () => clearTimeout(timeout);
  }, [editMode, openChatWindow]);

  const value = useMemo<IChatContext>(
    () => ({
      targetChatButton,
      setTargetChatButton,
      messageShowed,
      setMessageShowed,
      openChatWindow,
      setOpenChatWindow: setOpenChatWindowState,
      handleToggleWindow
    }),
    [targetChatButton, messageShowed, openChatWindow, handleToggleWindow]
  );

  return (
    <ChatContext.Provider value={value}>
      <WithTriggers
        triggers={triggers || []}
        onActivate={handleActivateTrigger}
      >
        {children}
      </WithTriggers>
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  return useContext<IChatContext>(ChatContext);
};
